import React, { useEffect, useMemo, useRef, useState } from "react";
import { Helmet } from "react-helmet-async";
import { useNavigate } from "react-router-dom";
import { getQuiz, submitDoshaQuiz } from "./index.js";
import "./DoshaQuiz.css";
import "./MiniDoshaQuiz.css";
import {
  initializeDoshaQuizGTM,
  trackPageView,
  trackQuizEvent,
  trackFormEvent,
  trackEngagement,
  trackConversion,
  trackError,
} from "../../utils/gtm";
import {
  captureUtmAttribution,
  captureLocationAsync,
  buildAttributionPayload,
} from "../../utils/utmAttribution";
import {
  MINI_QUIZ_UID,
  MINI_QUIZ_NAME,
  MINI_QUIZ_RESULT_PATH,
  MINI_QUIZ_COMPLETED_KEY,
  DEFAULT_MINI_QUIZ_LANGUAGE,
  MINI_QUIZ_FORM_LEFT_IMAGE,
  getMiniQuizLocale,
  sortMiniQuizFormFields,
  DOSHA_QUIZ_FAVICON_URL,
} from "./miniQuizConfig";
import { useDoshaQuizFavicon } from "./useDoshaQuizFavicon";

const DAYS = Array.from({ length: 31 }, (_, i) => i + 1);
const YEARS = Array.from({ length: 100 }, (_, i) => 2024 - i);

const renderPolicyText = (policyTextToRender, policyUrlToRender) => {
  if (!policyTextToRender) return null;

  const shouldCreateLink = !!policyUrlToRender;
  const regex = /<([^>]+)>(\*?)/g;
  const parts = [];
  let lastIndex = 0;
  let match;
  let linkKey = 0;

  while ((match = regex.exec(policyTextToRender)) !== null) {
    const matchIndex = match.index;
    const linkText = match[1];
    const hasAsterisk = match[2] === "*";

    if (matchIndex > lastIndex) {
      parts.push(policyTextToRender.substring(lastIndex, matchIndex));
    }

    if (shouldCreateLink) {
      parts.push(
        <a
          key={`link-${linkKey++}`}
          href={policyUrlToRender}
          target="_blank"
          rel="noopener noreferrer"
          className="privacy-policy-link"
        >
          {linkText}
        </a>
      );
    } else {
      parts.push(linkText);
    }

    if (hasAsterisk) parts.push("*");
    lastIndex = regex.lastIndex;
  }

  if (lastIndex < policyTextToRender.length) {
    parts.push(policyTextToRender.substring(lastIndex));
  }

  return parts.length ? parts : policyTextToRender;
};

const MiniDoshaQuiz = () => {
  useDoshaQuizFavicon({ restoreOnUnmount: false });

  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    language: DEFAULT_MINI_QUIZ_LANGUAGE,
    name: "",
    email: "",
    month: "",
    day: "",
    year: "",
    privacypolicy: false,
  });
  const [formErrors, setFormErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [apiAvailable, setApiAvailable] = useState(false);
  const [showQuiz, setShowQuiz] = useState(false);
  const [quizResponses, setQuizResponses] = useState({});
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [token, setToken] = useState(null);
  const [playerId, setPlayerId] = useState(null);
  const [location, setLocation] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [quizData, setQuizData] = useState(null);
  const [showTooltipForQuestion, setShowTooltipForQuestion] = useState(null);
  const [agentId] = useState("dosha_quiz" + Math.floor(Math.random() * 1000000));
  const [sessionId] = useState("dosha_quiz" + Math.floor(Math.random() * 1000000));
  const quizResponsesRef = useRef({});
  const currentQuestionIndexRef = useRef(0);
  const isManualNavigationRef = useRef(false);

  const locale = useMemo(
    () => getMiniQuizLocale(formData.language),
    [formData.language]
  );
  const formFields = useMemo(
    () => sortMiniQuizFormFields(locale.contentManagements),
    [locale.contentManagements]
  );
  const category = locale.category;
  const ui = locale.ui;

  useEffect(() => {
    quizResponsesRef.current = quizResponses;
  }, [quizResponses]);

  useEffect(() => {
    if (!showQuiz) return undefined;

    const container = document.querySelector(".mini-dosha-quiz .quiz-full-width");
    if (!container) return undefined;

    let cleanupFn = null;
    const timer = setTimeout(() => {
      const questionItems = container.querySelectorAll(".dosha-section .question-item");
      if (!questionItems.length) return;

      const observerOptions = {
        root: container,
        rootMargin: "-20% 0px -50% 0px",
        threshold: 0.1,
      };

      const observerCallback = (entries) => {
        if (isManualNavigationRef.current) return;

        let mostVisibleEntry = null;
        let maxIntersection = 0;

        entries.forEach((entry) => {
          if (entry.isIntersecting && entry.intersectionRatio > maxIntersection) {
            maxIntersection = entry.intersectionRatio;
            mostVisibleEntry = entry;
          }
        });

        if (mostVisibleEntry) {
          const questionIndex = Array.from(questionItems).indexOf(mostVisibleEntry.target);
          if (questionIndex !== -1 && questionIndex !== currentQuestionIndexRef.current) {
            currentQuestionIndexRef.current = questionIndex;
            setCurrentQuestionIndex(questionIndex);
          }
        }
      };

      const observer = new IntersectionObserver(observerCallback, observerOptions);
      questionItems.forEach((item) => observer.observe(item));

      const handleScroll = () => {
        if (isManualNavigationRef.current) return;

        const headerEl = container.querySelector(".quiz-header");
        const headerHeight = headerEl ? headerEl.getBoundingClientRect().height : 0;
        const viewportTop = container.scrollTop + headerHeight + 100;

        let closestIndex = 0;
        let closestDistance = Infinity;

        questionItems.forEach((item, index) => {
          const itemTop = item.offsetTop;
          const distance = Math.abs(itemTop - viewportTop);
          if (distance < closestDistance) {
            closestDistance = distance;
            closestIndex = index;
          }
        });

        if (closestIndex !== currentQuestionIndexRef.current) {
          currentQuestionIndexRef.current = closestIndex;
          setCurrentQuestionIndex(closestIndex);
        }
      };

      container.addEventListener("scroll", handleScroll, { passive: true });
      cleanupFn = () => {
        observer.disconnect();
        container.removeEventListener("scroll", handleScroll);
      };
    }, 100);

    return () => {
      clearTimeout(timer);
      if (cleanupFn) cleanupFn();
    };
  }, [showQuiz, category.questions.length]);

  useEffect(() => {
    captureUtmAttribution();
    captureLocationAsync();
    fetchQuizData();
    trackPageView("Dosha Quiz", window.location.pathname);
    initializeDoshaQuizGTM();
  }, []);

  const fetchQuizData = async () => {
    try {
      setLoading(true);
      setError(null);
      localStorage.removeItem(MINI_QUIZ_COMPLETED_KEY);
      const response = await getQuiz(MINI_QUIZ_UID);
      setApiAvailable(true);

      const apiLanguage = response?.data?.language;
      const initialLanguage =
        apiLanguage && getMiniQuizLocale(apiLanguage) ? apiLanguage : DEFAULT_MINI_QUIZ_LANGUAGE;

      if (response?.data?.token) setToken(response.data.token);
      if (response?.data?.location) setLocation(response.data.location);

      setFormData((prev) => ({
        ...prev,
        language: initialLanguage,
      }));

      const activeLocale = getMiniQuizLocale(initialLanguage);
      setQuizData({
        ...response,
        data: {
          ...(response?.data || {}),
          categories: [activeLocale.category],
          content_managements: activeLocale.contentManagements,
        },
      });

      trackQuizEvent("quiz_data_loaded", {
        quiz_uid: MINI_QUIZ_UID,
        categories_count: 1,
        has_token: !!response?.data?.token,
        language: initialLanguage,
      });
    } catch (err) {
      setError(err.message || "Failed to fetch quiz data");
      setApiAvailable(false);
      setQuizData({
        data: {
          categories: [getMiniQuizLocale(DEFAULT_MINI_QUIZ_LANGUAGE).category],
          content_managements: getMiniQuizLocale(DEFAULT_MINI_QUIZ_LANGUAGE).contentManagements,
        },
      });
      trackError("quiz_data_fetch_error", err.message || "Failed to fetch quiz data", {
        quiz_uid: MINI_QUIZ_UID,
        error_status: err.response?.status,
        api_available: false,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    trackFormEvent("field_interaction", {
      field_name: name,
      field_type: type,
      field_value: type === "checkbox" ? checked : value,
    });

    if (name === "name" && value.length === 1) {
      trackEngagement("form_started", {
        first_field: "name",
        form_type: "dosha_quiz_personal_info",
      });
    }

    if (type === "checkbox") {
      setFormData((prev) => ({ ...prev, [name]: checked }));
    } else {
      setFormData((prev) => ({ ...prev, [name]: value }));
    }

    setFormErrors((prev) => {
      const next = { ...prev };
      if (name === "month" || name === "day" || name === "year") {
        delete next.dob;
      } else {
        delete next[name];
      }
      return next;
    });
  };

  const handleLanguageChange = (e) => {
    const selectedLanguage = e.target.value;
    setFormData((prev) => ({ ...prev, language: selectedLanguage }));
    setFormErrors({});

    const nextLocale = getMiniQuizLocale(selectedLanguage);
    setQuizData((prev) => ({
      ...prev,
      data: {
        ...(prev?.data || {}),
        categories: [nextLocale.category],
        content_managements: nextLocale.contentManagements,
        language: selectedLanguage,
      },
    }));
  };

  const validatePersonalInfo = () => {
    const errors = {};

    formFields.forEach((field) => {
      if (field.content_type === "privacypolicy") {
        if (field.is_required && !formData.privacypolicy) {
          errors.privacypolicy = field.error_message || "You must accept the privacy policy.";
        }
        return;
      }

      if (field.content_type === "date-of-birth") {
        if ((formData.month || formData.day || formData.year) && (!formData.month || !formData.day || !formData.year)) {
          errors.dob = field.error_message || "Please select your full date of birth.";
        }
        return;
      }

      if (!field.is_required) return;

      const value = formData[field.name];
      if (field.content_type === "email") {
        if (!value?.trim()) {
          errors[field.name] = field.error_message || "Email is required.";
        } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) {
          errors[field.name] = field.error_message || "Enter a valid email address.";
        }
      } else if (field.content_type === "language") {
        if (!value?.trim()) {
          errors[field.name] = field.error_message || `${field.title || field.name} is required.`;
        }
      } else if (!value?.trim()) {
        errors[field.name] = field.error_message || `${field.title || field.name} is required.`;
      }
    });

    const isValid = Object.keys(errors).length === 0;
    trackFormEvent("form_validation", {
      is_valid: isValid,
      error_count: Object.keys(errors).length,
      error_fields: Object.keys(errors),
    });
    setFormErrors(errors);
    return isValid;
  };

  const calculateFormCompletion = () => {
    const required = formFields.filter((field) => field.is_required && field.content_type !== "date-of-birth");
    if (!required.length) return 0;

    const completed = required.filter((field) => {
      if (field.content_type === "privacypolicy") return !!formData.privacypolicy;
      return formData[field.name]?.toString().trim();
    }).length;

    return Math.round((completed / required.length) * 100);
  };

  const getDoshaCounts = (responses = quizResponsesRef.current) => {
    const counts = { vata: 0, pitta: 0, kapha: 0 };
    Object.values(responses).forEach((response) => {
      if (response?.dosha && counts[response.dosha] !== undefined) {
        counts[response.dosha] += 1;
      }
    });
    return counts;
  };

  const getDoshaFromQuestionIndex = (questionIndex, responses = quizResponsesRef.current) => {
    const question = category.questions[questionIndex];
    if (!question) return null;
    return responses[question.id]?.dosha || null;
  };

  const getDominantDosha = (responses = quizResponsesRef.current) => {
    const counts = getDoshaCounts(responses);
    const doshas = ["vata", "pitta", "kapha"];
    const highest = Math.max(...doshas.map((dosha) => counts[dosha]));
    const tiedDoshas = doshas.filter((dosha) => counts[dosha] === highest);

    if (tiedDoshas.length === 1) {
      return { dominantDosha: tiedDoshas[0], counts };
    }

    const firstQuestionDosha = getDoshaFromQuestionIndex(0, responses);
    if (firstQuestionDosha && tiedDoshas.includes(firstQuestionDosha)) {
      return { dominantDosha: firstQuestionDosha, counts };
    }

    const secondQuestionDosha = getDoshaFromQuestionIndex(1, responses);
    if (secondQuestionDosha) {
      return { dominantDosha: secondQuestionDosha, counts };
    }

    return { dominantDosha: tiedDoshas[0] || "vata", counts };
  };

  const buildSubmissionPayload = (status, responses = quizResponsesRef.current) => {
    const attributionPayload = buildAttributionPayload(location ? { location } : null);
    const counts = getDoshaCounts(responses);
    const doshaQuiz = {
      [category.name]: category.questions.map((question) => {
        const response = responses[question.id];
        return {
          question: question.content,
          answer: response?.answer || "",
          answer_type: question.answer_type,
          score: response?.score !== undefined ? response.score : 0,
        };
      }),
      vata_total_score: counts.vata,
      pitta_total_score: counts.pitta,
      kapha_total_score: counts.kapha,
    };

    return {
      attributionPayload,
      finalData: {
        quiz_name: MINI_QUIZ_NAME,
        request_type: "dosha_quiz",
        player_id: playerId,
        additional_data: {
          streaming: true,
          agent_id: agentId,
          session_id: sessionId,
          ...attributionPayload,
          email: formData.email.trim(),
          name: formData.name.trim(),
          ...(formData.month && formData.day && formData.year && {
            date_of_birth: `${formData.day}/${formData.month}/${formData.year}`,
          }),
          ...(location && { location }),
          language: formData.language,
          privacy_policy: formData.privacypolicy || false,
          status,
          dosha_quiz: doshaQuiz,
        },
      },
    };
  };

  const handleSubmit = async (e, status) => {
    if (e?.preventDefault) e.preventDefault();

    trackFormEvent("form_submission_attempt", {
      submission_status: status,
      form_data: {
        has_name: !!formData.name,
        has_email: !!formData.email,
        has_dob: !!(formData.month && formData.day && formData.year),
        privacy_policy_accepted: formData.privacypolicy || false,
      },
    });

    if (status === "personal_info") {
      const isValid = validatePersonalInfo();
      if (!isValid) {
        trackFormEvent("form_validation_failed", {
          error_fields: Object.keys(formErrors),
        });
        try {
          const firstErrorEl = document.querySelector(".field-error");
          if (firstErrorEl) firstErrorEl.scrollIntoView({ behavior: "smooth", block: "center" });
        } catch (_) {}
        return;
      }
      trackFormEvent("form_validation_success");
    }

    const { attributionPayload, finalData } = buildSubmissionPayload(status);

    try {
      if (status === "completed") {
        setIsSubmitting(true);
        const { dominantDosha, counts } = getDominantDosha();
        trackQuizEvent("quiz_completed", {
          dominant_dosha: dominantDosha,
          total_questions_answered: Object.keys(quizResponsesRef.current).length,
          dosha_scores: counts,
          completion_time: new Date().toISOString(),
        });
        trackConversion("dosha_quiz_completion", null);
        navigate(MINI_QUIZ_RESULT_PATH, {
          state: {
            finalData,
            utmAttribution: attributionPayload.attribution || {},
            quizData,
            userInfo: {
              token,
              name: formData.name.trim(),
              email: formData.email.trim(),
              dominantDosha,
              doshaCounts: counts,
              playerId,
              language: formData.language,
            },
          },
        });
        return;
      }

      const response = await submitDoshaQuiz(finalData, token);
      setPlayerId(response?.player?.id);
      trackFormEvent("form_submission_success", {
        player_id: response?.player?.id,
        submission_status: status,
      });
      setShowQuiz(true);
      setCurrentQuestionIndex(0);
      currentQuestionIndexRef.current = 0;
      trackPageView("Dosha Quiz Questions", window.location.pathname);
      trackQuizEvent("quiz_started", {
        total_categories: 1,
        player_id: response?.player?.id,
      });
    } catch (err) {
      console.error("Error submitting mini quiz:", err);
      trackError("quiz_submission_error", err.message || "Failed to submit quiz", {
        submission_status: status,
        player_id: playerId,
        error_type: err.name || "Unknown",
      });
      setError(err.response?.data?.message || err.message || "Failed to submit quiz");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleQuizAnswer = (question, option, index) => {
    const updated = {
      ...quizResponsesRef.current,
      [question.id]: {
        question: question.content,
        answer: option.label,
        answer_type: question.answer_type,
        score: index,
        dosha: option.dosha,
        key: option.key,
      },
    };
    quizResponsesRef.current = updated;
    setQuizResponses(updated);
    trackQuizEvent("question_answered", {
      category_name: category.name,
      question_id: question.id,
      answer_score: index,
      answer_text: option.label,
      answer_type: question.answer_type,
      question_text: question.content,
      category_progress: Object.keys(updated).length,
    });
  };

  const isQuestionAnswered = (question) => Boolean(quizResponses[question.id]?.answer);
  const allQuestionsAnswered = category.questions.every(isQuestionAnswered);

  const scrollToQuestion = (index) => {
    try {
      isManualNavigationRef.current = true;
      const container = document.querySelector(".mini-dosha-quiz .quiz-full-width");
      const questionItems = container?.querySelectorAll(".dosha-section .question-item");
      if (container && questionItems?.[index]) {
        const nextEl = questionItems[index];
        nextEl.classList.add("slide-up");
        currentQuestionIndexRef.current = index;
        setCurrentQuestionIndex(index);
        nextEl.scrollIntoView({ behavior: "smooth", block: "start" });
        setTimeout(() => nextEl.classList.remove("slide-up"), 450);
        setTimeout(() => {
          isManualNavigationRef.current = false;
        }, 600);
      }
    } catch (_) {
      isManualNavigationRef.current = false;
    }
  };

  const renderFormField = (field) => {
    const name = field.name;
    const required = field.is_required;

    if (field.content_type === "language") {
      return (
        <div key={field.id} className="form-group">
          <label htmlFor={name}>
            {field.title}
            {required && <span className="required-indicator"> *</span>}
          </label>
          <select
            id={name}
            name={name}
            value={formData.language}
            onChange={handleLanguageChange}
            required={required}
          >
            {field.options.map((option) => (
              <option key={option.id} value={option.option}>
                {option.option}
              </option>
            ))}
          </select>
          {formErrors[name] && <div className="field-error">{formErrors[name]}</div>}
        </div>
      );
    }

    if (field.content_type === "date-of-birth") {
      const months = field.options?.map((opt) => opt.option) || [];
      return (
        <div key={field.id} className="form-group">
          <label>
            {field.title}
            {required && <span className="required-indicator"> *</span>}
          </label>
          <div className="dob-row">
            <select name="month" value={formData.month} onChange={handleInputChange} required={required}>
              <option value="">{ui.dobMonth}</option>
              {months.map((month) => (
                <option key={month} value={month}>
                  {month}
                </option>
              ))}
            </select>
            <select name="day" value={formData.day} onChange={handleInputChange} required={required}>
              <option value="">{ui.dobDay}</option>
              {DAYS.map((day) => (
                <option key={day} value={day}>
                  {day}
                </option>
              ))}
            </select>
            <select name="year" value={formData.year} onChange={handleInputChange} required={required}>
              <option value="">{ui.dobYear}</option>
              {YEARS.map((year) => (
                <option key={year} value={year}>
                  {year}
                </option>
              ))}
            </select>
          </div>
          {formErrors.dob && <div className="field-error">{formErrors.dob}</div>}
        </div>
      );
    }

    if (field.content_type === "privacypolicy") {
      const policyTextContent = renderPolicyText(field.description, field.urls);
      return (
        <div key={field.id} className="form-group privacy-group">
          <div className="checkbox-option">
            <input
              type="checkbox"
              className="privacy-consent-checkbox"
              id={name}
              name={name}
              checked={formData.privacypolicy || false}
              onChange={handleInputChange}
              required={required}
            />
            <label htmlFor={name}>
              {policyTextContent || field.description}
              {required && <span className="required-indicator"> *</span>}
            </label>
          </div>
          {formErrors.privacypolicy && <div className="field-error">{formErrors.privacypolicy}</div>}
        </div>
      );
    }

    return (
      <div key={field.id} className="form-group">
        <label htmlFor={name}>
          {field.title}
          {required && <span className="required-indicator"> *</span>}
        </label>
        <input
          type={field.content_type === "email" ? "email" : "text"}
          id={name}
          name={name}
          value={formData[name] || ""}
          onChange={handleInputChange}
          required={required}
          placeholder={field.description}
        />
        {formErrors[name] && <div className="field-error">{formErrors[name]}</div>}
      </div>
    );
  };

  return (
    <>
      <Helmet>
        <title>{ui.pageTitle}</title>
        <meta name="description" content={ui.pageDescription} />
        <meta property="og:title" content={ui.pageTitle} />
        <meta property="og:description" content={ui.pageDescription} />
        <meta property="og:type" content="website" />
        <link rel="icon" href={DOSHA_QUIZ_FAVICON_URL} />
      </Helmet>
      <div className="dosha-quiz-container mini-dosha-quiz">
        {!showQuiz ? (
          <div className="quiz-layout quiz-layout--custom-image">
            <div className="quiz-left quiz-left--custom-image">
              <img
                src={MINI_QUIZ_FORM_LEFT_IMAGE}
                alt={ui.introSubtitle}
                className="quiz-left-image"
              />
            </div>
            <div className="quiz-right">
              <div className="form-card">
                {loading ? (
                  <div className="loading-message">
                    <div className="loading-spinner-small"></div>
                    <p>{ui.loading}</p>
                  </div>
                ) : (
                  <>
                    {error && (
                      <div className="error-message">
                        <p>{error}</p>
                        {!apiAvailable && (
                          <p style={{ fontSize: "12px", opacity: 0.8 }}>
                            Note: You can still complete the form without API connection.
                          </p>
                        )}
                        <button onClick={() => window.location.reload()} className="retry-btn">
                          Retry
                        </button>
                      </div>
                    )}
                    <div className="mini-quiz-intro">
                      <h1>{ui.introSubtitle}</h1>
                      <p>{ui.introInstructions}</p>
                    </div>
                    <form className="quiz-form">
                      {formFields.map(renderFormField)}
                      <button
                        type="button"
                        className="submit-btn"
                        onClick={(e) => {
                          trackFormEvent("form_submit_button_clicked", {
                            button_text: ui.continueToQuiz,
                            form_completion_percentage: calculateFormCompletion(),
                          });
                          trackEngagement("continue_to_quiz_clicked", {
                            form_completion_percentage: calculateFormCompletion(),
                            has_name: !!formData.name,
                            has_email: !!formData.email,
                            has_dob: !!(formData.month && formData.day && formData.year),
                          });
                          handleSubmit(e, "personal_info");
                        }}
                      >
                        {ui.continueToQuiz}
                      </button>
                    </form>
                  </>
                )}
              </div>
            </div>
          </div>
        ) : (
          <div className="quiz-full-width">
            <div className="quiz-header">
              <h2>{ui.introSubtitle}</h2>
              <p className="quiz-instructions">{ui.introInstructions}</p>
              <div className="quiz-progress">
                <span>
                  {ui.questionOf} {currentQuestionIndex + 1} {ui.of} {category.questions.length}
                </span>
              </div>
            </div>
            <div className="dosha-section last-category">
              {category.questions.map((question, index) => {
                const isLastQuestion = index === category.questions.length - 1;
                const showQuestionNav = index > 0 || !isLastQuestion;

                return (
                <div
                  key={question.id}
                  className={`question-item ${isLastQuestion ? "last-question" : ""}`}
                >
                  {showTooltipForQuestion === question.id && !isQuestionAnswered(question) && (
                    <div className="next-btn-tooltip">{ui.selectResponseTooltip}</div>
                  )}
                  <div className="question-text">{question.content}</div>
                  <div className="answer-options mini-quiz-options">
                    {question.options.map((option, optionIndex) => (
                      <label key={option.key} className="mini-quiz-option">
                        <input
                          type="radio"
                          name={`question_${question.id}`}
                          value={option.key}
                          checked={quizResponses[question.id]?.key === option.key}
                          onChange={() => {
                            handleQuizAnswer(question, option, optionIndex);
                            if (index < category.questions.length - 1) {
                              scrollToQuestion(index + 1);
                            }
                          }}
                        />
                        <span>{option.label}</span>
                      </label>
                    ))}
                  </div>
                  {(showQuestionNav || isLastQuestion) && (
                    <div
                      className={`question-navigation-btn-container${
                        isLastQuestion ? " mini-last-question-nav" : ""
                      }`}
                    >
                      {index > 0 && (
                        <button className="submit-btn prev-btn" onClick={() => scrollToQuestion(index - 1)}>
                          {ui.previousQuestion}
                        </button>
                      )}
                      {!isLastQuestion && (
                        <div
                          className="next-btn-wrapper"
                          onMouseDown={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            if (!isQuestionAnswered(question)) {
                              setShowTooltipForQuestion(question.id);
                              setTimeout(() => setShowTooltipForQuestion(null), 5000);
                            } else {
                              scrollToQuestion(index + 1);
                            }
                          }}
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            if (!isQuestionAnswered(question)) {
                              setShowTooltipForQuestion(question.id);
                              setTimeout(() => setShowTooltipForQuestion(null), 5000);
                            } else {
                              scrollToQuestion(index + 1);
                            }
                          }}
                        >
                          <button
                            className="submit-btn next-btn"
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                            }}
                            disabled={!isQuestionAnswered(question)}
                          >
                            {ui.nextQuestion}
                          </button>
                        </div>
                      )}
                      {isLastQuestion && (
                        <button
                          className="submit-btn mini-submit-quiz-btn"
                          onClick={(e) => {
                            trackQuizEvent("quiz_submission_clicked", {
                              category_name: category.name,
                              total_questions_answered: Object.keys(quizResponses).length,
                            });
                            trackEngagement("quiz_submission_clicked", {
                              category_name: category.name,
                              total_questions_answered: Object.keys(quizResponses).length,
                              final_category: 0,
                            });
                            handleSubmit(e, "completed");
                          }}
                          disabled={!allQuestionsAnswered || isSubmitting}
                        >
                          {isSubmitting ? (
                            <>
                              <div className="loading-spinner-small"></div>
                              {ui.submittingQuiz}
                            </>
                          ) : (
                            ui.submitQuiz
                          )}
                        </button>
                      )}
                    </div>
                  )}
                </div>
              );
              })}
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default MiniDoshaQuiz;
